import { computed, type ComputedRef } from 'vue';
import type { FilterConfig } from '@/components/design/composite/GaiaFilterPanel.vue';
import { useKanbanStore } from '@/stores/kanbanStore';
import { useOrganizationStore } from '@/stores/organizationStore';

/** `GaiaFilterPanel` shows backend `null` as this — a query string has no null. */
const EMPTY_SENTINEL = '(Empty)';

/** Sentinel for "no assignee"; not a real address, mapped back to `null` on write. */
export const UNASSIGNED_VALUE = 'unassigned';

/** The AI agent's account, shown as a name rather than an address. */
export const AGENT_EMAIL = 'agent@gaia.law';
export const AGENT_LABEL = 'Gaia AI Agent';

export type ReviewFilterOptions = Record<string, (string | null)[]>;

/**
 * Coerce the panel's raw filter state into what `searchReview` accepts: the
 * `(Empty)` display value stands for backend `null`, which `column_filters`
 * cannot carry, so it is dropped along with any key left empty by the drop.
 */
export function buildColumnFilters(active: Record<string, unknown>): Record<string, string[]> {
  const filters: Record<string, string[]> = {};

  for (const [key, value] of Object.entries(active)) {
    if (!Array.isArray(value) || value.length === 0) continue;

    const values = value
      .map(entry => (entry === EMPTY_SENTINEL ? null : entry))
      .filter((entry): entry is string => typeof entry === 'string' && entry.length > 0);

    if (values.length > 0) filters[key] = values;
  }

  return filters;
}

/** Union of two option maps, order-preserving and deduped. */
export function mergeFilterOptions(
  base: ReviewFilterOptions | undefined,
  incoming: ReviewFilterOptions
): ReviewFilterOptions {
  const merged: ReviewFilterOptions = { ...base };
  for (const [key, values] of Object.entries(incoming)) {
    const seen = [...(merged[key] ?? [])];
    for (const value of values) {
      if (!seen.includes(value)) seen.push(value);
    }
    merged[key] = seen;
  }
  return merged;
}

/**
 * Flatten the board's per-column option maps into the one map the filter panel
 * takes. The board fetches a page per column and each response reports the
 * options for *that column only*, so these must be unioned: taking the last
 * response's options meant the menu only ever described whichever column
 * resolved last, and an assignee appearing solely in Done went missing.
 */
export function unionFilterOptions(
  perState: Record<string, ReviewFilterOptions>
): ReviewFilterOptions {
  let merged: ReviewFilterOptions = {};
  for (const perColumn of Object.values(perState)) {
    merged = mergeFilterOptions(merged, perColumn);
  }
  return merged;
}

/**
 * Normalize a search response's `filter_options` into a plain option map,
 * tolerating the keys the endpoint omits when a column has no values.
 */
export function normalizeFilterOptions(
  raw: Record<string, (string | null)[] | undefined> | null | undefined
): ReviewFilterOptions {
  const result: ReviewFilterOptions = {};
  if (!raw) return result;
  for (const [key, values] of Object.entries(raw)) {
    if (values && values.length > 0) result[key] = [...values];
  }
  return result;
}

export interface UseReviewFilterLabelsReturn {
  filterConfig: ComputedRef<FilterConfig[]>;
  valueLabelMap: ComputedRef<Record<string, Record<string | number, string>>>;
}

/**
 * The filterable columns and their display labels, shared by both halves of the
 * reviews list. `kanban_state_uid` is only offered in table mode: on the board
 * it narrows *which columns render* rather than which rows match, because each
 * column already forces its own `kanban_state_uid` into the query.
 */
export function useReviewFilterLabels(
  options: { includeKanbanState?: boolean } = {}
): UseReviewFilterLabelsReturn {
  const { includeKanbanState = true } = options;
  const organizationStore = useOrganizationStore();
  const kanbanStore = useKanbanStore();

  const filterConfig = computed<FilterConfig[]>(() => {
    const config: FilterConfig[] = [{ key: 'assignee_user_email', label: 'Assigned User' }];
    if (includeKanbanState) config.push({ key: 'kanban_state_uid', label: 'Kanban State' });
    return config;
  });

  const valueLabelMap = computed<Record<string, Record<string | number, string>>>(() => {
    const map: Record<string, Record<string | number, string>> = {
      assignee_user_email: {
        [UNASSIGNED_VALUE]: 'Unassigned',
        [AGENT_EMAIL]: AGENT_LABEL,
      },
      kanban_state_uid: {},
    };

    for (const user of organizationStore.users) {
      map.assignee_user_email[user.email] = user.full_name || user.email;
    }

    for (const state of kanbanStore.sortedKanbanStates) {
      map.kanban_state_uid[state.uid] = state.name;
    }
    map.kanban_state_uid[EMPTY_SENTINEL] = EMPTY_SENTINEL;

    return map;
  });

  return { filterConfig, valueLabelMap };
}
